import {
  AutocompleteInputForm,
  Button,
  CheckboxForm,
  TextInputForm,
  Typography,
} from '@altify/components';
import { useContent } from '@altify/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createFileRoute,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import useCountry from 'hooks/useCountry';
import { useForm } from 'react-hook-form';
import {
  useCreateOrUpdateCustomerPersonalDataMutation,
  useGetPersonalDataQuery,
} from 'store/slices/crypto-accounts-api';
import * as z from 'zod';
import CryptoWithdrawalWrapper from '../../../components/CryptoWithdrawalWrapper';
import { content } from './route.content';

export const Route = createFileRoute(
  '/_authenticated/funds/withdraw/crypto/$productCode/wallet/edit/travel-rule',
)({
  component: TravelRule,
});

const schema = z.object({
  birthCity: z.string().min(1).max(255),
  birthCountryCode: z.string().min(1),
  residencyCountryCode: z.string().min(1),
  residencyCity: z.string().min(1).max(255),
  streetName: z.string().min(1).max(255),
  streetNumber: z.string().min(1).max(255),
  zipCode: z.string().min(1).max(10),
  confirmation: z
    .boolean()
    .refine(v => v == true, { message: 'Confirmation is required' })
    .default(false),
});
type FormSchema = z.infer<typeof schema>;

function TravelRule() {
  const localizedContent = useContent(content);

  const navigate = useNavigate();
  const location = useLocation();
  const { productCode } = Route.useParams();
  const countries = useCountry();
  const countriesList = countries?.map(c => ({
    label: c.name,
    value: c.code,
  }));

  const { data, isLoading } = useGetPersonalDataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [createOrUpdatePersonalData, createOrUpdatePersonalDataResults] =
    useCreateOrUpdateCustomerPersonalDataMutation();

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm<FormSchema>({
    resolver: zodResolver(schema),
    values: {
      birthCity: data?.birthCity ?? '',
      birthCountryCode: data?.birthCountryCode ?? '',
      residencyCity: data?.residencyCity ?? '',
      residencyCountryCode: data?.residencyCountryCode ?? '',
      streetName: data?.streetName ?? '',
      streetNumber: data?.streetNumber ?? '',
      zipCode: data?.zipCode ?? '',
      confirmation: false,
    },
  });

  const onSubmit = handleSubmit(async data => {
    const result = await createOrUpdatePersonalData(data);
    if (!result.error) {
      navigate({
        to: `/funds/withdraw/crypto/${productCode}/wallet/edit/details`,
      });
    }
  });

  const bodyContent = (
    <>
      <Typography size="large" className="w-full">
        {localizedContent.birthDetails}
      </Typography>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <TextInputForm
          control={control}
          name="birthCity"
          label={localizedContent.cityOfBirth}
        />
        <AutocompleteInputForm
          control={control}
          name="birthCountryCode"
          items={countriesList}
          label={localizedContent.countryOfBirth}
        />
      </div>
      <Typography size="large" className="w-full">
        {localizedContent.address}
      </Typography>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <TextInputForm
          control={control}
          name="streetName"
          label={localizedContent.streetName}
        />
        <TextInputForm
          control={control}
          name="streetNumber"
          label={localizedContent.streetNumber}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <TextInputForm
          control={control}
          name="residencyCity"
          label={localizedContent.residencyCity}
        />
        <TextInputForm
          control={control}
          name="zipCode"
          label={localizedContent.postalCode}
        />
      </div>
      <AutocompleteInputForm
        control={control}
        name="residencyCountryCode"
        items={countriesList}
        label={localizedContent.residencyCountry}
      />

      <CheckboxForm
        className="mt-4"
        control={control}
        name="confirmation"
        label={localizedContent.confirmation}
      />
    </>
  );

  const buttons = (
    <div className="pb-20 w-full">
      <Button
        loading={createOrUpdatePersonalDataResults.isLoading}
        onPress={onSubmit}
        text={localizedContent.next}
        disabled={!isDirty || !isValid}
        className="w-full"
      />
    </div>
  );
  return (
    <CryptoWithdrawalWrapper
      isLoading={isLoading || !countriesList?.length}
      title={localizedContent.title}
      description={localizedContent.description}
      bodyContent={bodyContent}
      buttons={buttons}
    />
  );
}

export default TravelRule;
